import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useOrg } from "@/context/OrgContext";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/common/PageHeader";
import { PageLoading } from "@/components/common/Loading";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { TeamOverviewTab } from "@/components/workspace/team/TeamOverviewTab";
import { PeopleTab } from "@/components/workspace/team/PeopleTab";
import { AssignmentsTab } from "@/components/workspace/team/AssignmentsTab";
import { ResponsibilitiesTab } from "@/components/workspace/team/ResponsibilitiesTab";
import { ApprovalsTab } from "@/components/workspace/team/ApprovalsTab";
import { TeamKpisTab, TeamResourcesTab } from "@/components/workspace/team/TeamKpisTab";
import { TeamLeaveTab } from "@/components/people/team/TeamLeaveTab";
import { TeamTimeTab } from "@/components/people/team/TeamTimeTab";
import { canAdminPeople, isPeopleAdmin } from "@/lib/workspace";
import { leaveOn, clockOn } from "@/lib/people";

export default function TeamManagement() {
  const { orgId, currentOrg } = useOrg();
  const { user, canEdit } = useAuth();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "overview";
  const owner = params.get("owner") || "all";
  const go = (t, extra = {}) => setParams({ tab: t, ...extra }, { replace: true });
  const [overview, setOverview] = useState(null);
  const [approvals, setApprovals] = useState(null);
  const [members, setMembers] = useState([]);
  const [people, setPeople] = useState([]);

  const refresh = () => {
    api.get(`/workspace/team/overview?org_id=${orgId}`).then((r) => setOverview(r.data));
    api.get(`/workspace/approvals?org_id=${orgId}`).then((r) => setApprovals(r.data));
  };
  useEffect(() => {
    if (!orgId) return;
    refresh();
    api.get(`/organisations/${orgId}/members`).then((r) => setMembers(r.data));
    api.get(`/workspace/people?org_id=${orgId}`).then((r) => setPeople(r.data));
  }, [orgId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!overview || !approvals) return <PageLoading />;
  const pending = approvals.reviews.length + approvals.kpi_verifications.length
    + (approvals.leave_requests?.length || 0) + (approvals.time_corrections?.length || 0);
  const admin = canAdminPeople(user);
  const showLeave = leaveOn(currentOrg);
  const showTime = clockOn(currentOrg);

  return (
    <div data-testid="team-page">
      <PageHeader eyebrow={currentOrg?.company_name} title="Team" icon="Network"
        subtitle={overview.scope === "all" ? "Everyone in the company — assign linked work, keep recurring duties running and check results independently." : "The people who report to you — assign linked work, keep recurring duties running and check results independently."} />
      <Tabs value={tab} onValueChange={(t) => go(t)}>
        <TabsList className="mb-5 flex h-auto flex-wrap justify-start bg-white">
          <TabsTrigger value="overview" data-testid="team-tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="people" data-testid="team-tab-people">People</TabsTrigger>
          <TabsTrigger value="work" data-testid="team-tab-work">Assigned work</TabsTrigger>
          <TabsTrigger value="recurring" data-testid="team-tab-recurring">Recurring</TabsTrigger>
          <TabsTrigger value="approvals" data-testid="team-tab-approvals">Approvals{pending ? ` (${pending})` : ""}</TabsTrigger>
          <TabsTrigger value="kpis" data-testid="team-tab-kpis">KPI results</TabsTrigger>
          {showLeave && <TabsTrigger value="leave" data-testid="team-tab-leave">Leave</TabsTrigger>}
          {showTime && <TabsTrigger value="time" data-testid="team-tab-time">Time</TabsTrigger>}
          <TabsTrigger value="resources" data-testid="team-tab-resources">Resources</TabsTrigger>
        </TabsList>
        <TabsContent value="overview"><TeamOverviewTab data={overview} onOpenPerson={(id) => go("work", { owner: id })} /></TabsContent>
        <TabsContent value="people"><PeopleTab orgId={orgId} canAdmin={admin && canEdit} isPeopleAdmin={isPeopleAdmin(user)} members={members} /></TabsContent>
        <TabsContent value="work">
          <AssignmentsTab orgId={orgId} people={people} ownerFilter={owner} canEdit={canEdit} onChanged={refresh}
            onOwnerFilter={(id) => go("work", id === "all" ? {} : { owner: id })} />
        </TabsContent>
        <TabsContent value="recurring"><ResponsibilitiesTab orgId={orgId} people={people} canEdit={canEdit} onChanged={refresh} /></TabsContent>
        <TabsContent value="approvals"><ApprovalsTab data={approvals} onChanged={refresh} /></TabsContent>
        <TabsContent value="kpis"><TeamKpisTab orgId={orgId} meId={user?.id} canEdit={canEdit} onChanged={refresh} /></TabsContent>
        {showLeave && <TabsContent value="leave"><TeamLeaveTab orgId={orgId} members={members} onChanged={refresh} /></TabsContent>}
        {showTime && <TabsContent value="time"><TeamTimeTab orgId={orgId} /></TabsContent>}
        <TabsContent value="resources"><TeamResourcesTab orgId={orgId} canAdmin={admin && canEdit} /></TabsContent>
      </Tabs>
    </div>
  );
}
